import { useState, useEffect, useRef, useCallback } from 'react';
import { Input, Tooltip } from "reactstrap";
import { formatToGbIsoDateTime } from "../../../utils/dateUtils";
import Select, { components } from 'react-select';
import { useTheme } from '../../../hooks/useTheme';

// Coerce a value to a boolean, handling legacy string representations that may
// exist if an attribute was previously a lookup (e.g. "Yes"/"No") and was later
// changed to a boolean type.
const coerceToBool = (v) => {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v !== 0;
    if (typeof v === 'string') {
        const lower = v.trim().toLowerCase();
        if (lower === 'true' || lower === 'yes' || lower === '1') return true;
        if (lower === 'false' || lower === 'no' || lower === '0' || lower === '') return false;
    }
    return !!v;
};

export default function ItemTypeCustomAttributes({ label, type, required, handleChange, value, lookupChoices, disabled = false, title = null }) {
    const theme = useTheme();
    const isInvalid = required && (value === null || value === undefined || value === '');

    // Buffer edits locally so the input shows what the user types.
    // The parent's handleChange is called after a short debounce, or immediately on blur.
    const initialValue = type === 'boolean' ? coerceToBool(value) : value;
    const [localValue, setLocalValue] = useState(initialValue);
    const [iconHover, setIconHover] = useState(false);
    const localValueRef = useRef(initialValue);
    const isFocusedRef = useRef(false);
    const debounceTimerRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!isFocusedRef.current) {
            const incoming = type === 'boolean' ? coerceToBool(value) : value;
            setLocalValue(incoming);
            localValueRef.current = incoming;
        }
    }, [value, type]);

    // Clean up any pending timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, []);

    const debouncedCommit = useCallback((fieldType) => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null;
            handleChange({ target: { name: label, value: localValueRef.current } }, fieldType);
        }, 200);
    }, [handleChange, label]);

    const handleLocalChange = (e, fieldType) => {
        const v = e.target.value;
        setLocalValue(v);
        localValueRef.current = v;
        debouncedCommit(fieldType);
    };

    const handleInputFocus = () => { isFocusedRef.current = true; };

    const commitOnBlur = (fieldType) => {
        isFocusedRef.current = false;
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        handleChange({ target: { name: label, value: localValueRef.current } }, fieldType);
    };

    const customStyles = {
        option: (styles, { isSelected }) => ({
            ...styles,
            fontFamily: "Hanken Grotesk, Sans-serif",
            fontWeight: isSelected ? "bold" : "normal",
            color: isSelected ? "#fff" : "",
            lineHeight: "0.8em",
        }),
        control: (styles) => ({
            ...styles,
            borderColor: "transparent",
            boxShadow: "none",
            minHeight: "auto",
            height: "calc(1.5em + 0.75rem)",
            "&:hover": { borderColor: "transparent" },
        }),
        valueContainer: (styles) => ({
            ...styles,
            padding: "0 8px",
            height: "100%",
        }),
        input: (styles) => ({
            ...styles,
            margin: 0,
            padding: 0,
        }),
        singleValue: (styles) => ({
            ...styles,
            margin: 0,
            textAlign: "left",
        }),
        dropdownIndicator: (styles) => ({
            ...styles,
            padding: "2px 4px",
            color: theme === "dark-mode" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.4)",
            "&:hover": { color: theme === "dark-mode" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.6)" },
        }),
        indicatorSeparator: (styles) => ({
            ...styles,
            backgroundColor: "transparent",
        }),
    };

    const customTheme = {
        ...theme,
        colors: {
            ...theme.colors,
            primary25: "#B2D953",
            primary: theme === "dark-mode" ? "#2c414b" : "#dee2e6",
            neutral0: theme === "dark-mode" ? "#1b272d" : "#E4E4E4",
        },
    };

    const tooltipMessage = title ?? ((type === 'text' || type === 'lookup') && localValue ? String(localValue) : null);

    return (
        <div
            ref={containerRef}
            style={{ width: "100%" }}
            onMouseEnter={() => setIconHover(true)}
            onMouseLeave={() => setIconHover(false)}
        >
            {type === 'text' &&
                <Input
                    id={label}
                    name={label}
                    type="text"
                    onChange={(e) => handleLocalChange(e, 'text')}
                    onFocus={handleInputFocus}
                    onBlur={() => commitOnBlur('text')}
                    value={localValue ?? ''}
                    invalid={isInvalid}
                    disabled={disabled}
                />
            }
            {type === 'integer' &&
                <Input
                    id={label}
                    name={label}
                    type="number"
                    step="1"
                    style={{ textAlign: "right" }}
                    onChange={(e) => handleLocalChange(e, 'integer')}
                    onFocus={handleInputFocus}
                    onBlur={() => commitOnBlur('integer')}
                    value={localValue ?? ''}
                    invalid={isInvalid}
                    disabled={disabled}
                />
            }
            {type === 'datetime' &&
                <Input
                    id={label}
                    name={label}
                    type="datetime-local"
                    step="1"
                    style={{ textAlign: "right" }}
                    onChange={(e) => handleLocalChange(e, 'datetime')}
                    onFocus={handleInputFocus}
                    onBlur={() => commitOnBlur('datetime')}
                    value={localValue ? formatToGbIsoDateTime(localValue) : ""}
                    invalid={isInvalid}
                    disabled={disabled}
                />
            }
            {type === 'boolean' &&
                <Input
                    id={label}
                    name={label}
                    type="checkbox"
                    onChange={(e) => {
                        const newVal = e.target.checked;
                        setLocalValue(newVal);
                        localValueRef.current = newVal;
                        handleChange({ target: { name: label, checked: newVal } }, 'boolean');
                    }}
                    checked={coerceToBool(localValue)}
                    invalid={isInvalid}
                    disabled={disabled}
                />
            }
            {type === 'decimal' &&
                <Input
                    id={label}
                    name={label}
                    type="number"
                    step="any"
                    style={{ textAlign: "right" }}
                    onChange={(e) => handleLocalChange(e, 'decimal')}
                    onFocus={handleInputFocus}
                    onBlur={() => commitOnBlur('decimal')}
                    value={localValue ?? ''}
                    invalid={isInvalid}
                    disabled={disabled}
                />
            }
            {type === 'lookup' && disabled &&
                <Input
                    id={label}
                    name={label}
                    type="text"
                    value={localValue ?? ''}
                    invalid={isInvalid}
                    disabled={true}
                />
            }
            {type === 'lookup' && !disabled &&
                <Select
                    id={label}
                    isSearchable={true}
                    options={lookupChoices.map((m) => { return { label: m, value: m } })}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                        ...customStyles,
                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({
                            ...base,
                            zIndex: 9999,
                            width: 'auto',        // let it size to content
                            minWidth: base.width, // at least as wide as the control
                        })
                    }}
                    value={localValue != null ? { value: localValue, label: localValue } : null}
                    theme={customTheme}
                    onChange={(e) => {
                        setLocalValue(e.value);
                        localValueRef.current = e.value;
                        handleChange({ target: { name: label, value: e.value } }, 'lookup');
                    }}
                    components={{
                        // Tooltip for each option in the dropdown menu
                        Option: (props) => (
                            <components.Option
                                {...props}
                                innerProps={{ ...props.innerProps, title: props.data.label }}
                            />
                        ),
                    }}
                />
            }
            {tooltipMessage && iconHover && containerRef.current && (
                <Tooltip isOpen={true} target={containerRef} placement="top">{tooltipMessage}</Tooltip>
            )}
        </div>
    );
}
